import keyBy from "lodash/keyBy";
import { IPagination } from "./usePagination";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSingleton } from "../../other/injection";
import { MessageBusService } from "../../other/messageBus/MessageBusService";

interface IResultState<T> {
    hasResult: boolean;
    items: T[];
    infoReceivedForPage: number;
    dataReceivedForPage: number;
    isLastPage: boolean;
    resultSetId: number;
}

export interface IApiResult<TItem, TId> {
    IsUpdate: boolean | undefined;
    Items: TItem[] | undefined;
    Removed: TId[] | undefined;
    SubscriptionId?: string;
}

interface IProps<TItem, TId> {
    createSubscription: (
        callback: (x: IApiResult<TItem, TId>) => void
    ) => () => void;
    getItemId: (x: TItem) => TId;
    sortCompare: (a: TItem, b: TItem) => 1 | 0 | -1;
    disabled?: boolean;
}

interface IOutput<TItem> {
    hasResult: boolean;
    items: TItem[];
    pagination: IPagination;
    resultSetId: number;
    reset: () => void;
}

export function useSubscription<TItem, TId extends string | number>({
    createSubscription,
    getItemId,
    sortCompare,
    disabled
}: IProps<TItem, TId>): IOutput<TItem> {
    const messageBusService = useSingleton(MessageBusService);

    const resultSetRef = useRef(0);
    const requestingPageRef = useRef(0);
    const subscriptionIdRef = useRef<string>();

    const [result, setResult] = useState<IResultState<TItem>>({
        hasResult: false,
        items: [],
        infoReceivedForPage: 0,
        dataReceivedForPage: 0,
        isLastPage: false,
        resultSetId: 0
    });

    const requestAnotherPage = useCallback(async () => {
        requestingPageRef.current++;
        const subscriptionId = subscriptionIdRef.current;
        const request: RedDeer.Contracts.Queries.StreamComposer.NextPageQuery =
            {
                MessageType: "Queries.StreamComposer.NextPageQuery",
                SubscriptionId: subscriptionId
            };
        const response =
            await messageBusService.asyncSendPromise<RedDeer.Contracts.Queries.StreamComposer.NextPageResponse>(
                {
                    request,
                    description: "Requesting next page for subscription"
                }
            );
        if (subscriptionId !== subscriptionIdRef.current) {
            // subscription has changed since making next page request
            return;
        }
        setResult(old => ({
            ...old,
            infoReceivedForPage: requestingPageRef.current,
            isLastPage: response.IsLastPage ?? old.isLastPage
        }));
    }, [messageBusService]);

    const pagination = useMemo(
        () => ({
            requestAnotherPage,
            currentPage: Math.min(
                result.infoReceivedForPage,
                result.dataReceivedForPage
            ), // both info & data page numbers need to be increased for the actual page to be considered ready
            isLastPage: result.isLastPage
        }),
        [
            requestAnotherPage,
            result.dataReceivedForPage,
            result.infoReceivedForPage,
            result.isLastPage
        ]
    );

    const [resetToken, setResetToken] = useState(0);
    const reset = useCallback(() => {
        setResetToken(x => x + 1);
    }, []);

    useEffect(() => {
        if (disabled) return;

        resultSetRef.current++; // new subscription dependency suggests a new result set
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        resetToken; // reference resetToken so that it gets included in dependency array

        return createSubscription(response => {
            const subscriptionId = response.SubscriptionId;
            subscriptionIdRef.current = subscriptionId;
            setResult(old => {
                const existingIds = keyBy(old.items, getItemId);
                const removeIds = keyBy(response.Removed ?? [], x => x);
                const newIds = keyBy(response.Items ?? [], getItemId);

                const items = (
                    response.IsUpdate
                        ? [
                              ...old.items.map(x => newIds[getItemId(x)] ?? x),
                              ...(response.Items ?? []).filter(
                                  x => !(getItemId(x) in existingIds)
                              )
                          ]
                        : (response.Items ?? [])
                ).filter(x => !(getItemId(x) in removeIds));
                items.sort(sortCompare);

                if (!response.IsUpdate) {
                    requestingPageRef.current++; // increase page number for initial result
                }

                return {
                    hasResult: true,
                    items,
                    resultSetId: resultSetRef.current,
                    dataReceivedForPage: requestingPageRef.current,
                    ...(response.IsUpdate
                        ? {
                              infoReceivedForPage: old.infoReceivedForPage,
                              isLastPage: old.isLastPage
                          }
                        : {
                              infoReceivedForPage: requestingPageRef.current,
                              isLastPage: subscriptionId ? false : true
                          })
                };
            });
        });
    }, [createSubscription, disabled, getItemId, resetToken, sortCompare]);

    return {
        hasResult: result.hasResult,
        items: result.items,
        pagination,
        resultSetId: result.resultSetId,
        reset
    };
}
